/**
 * Re-timing a route's stops when one of them is dragged: the moved stop takes its new times, the
 * others are pushed clear of it, and any overlaps that leaves are cascaded forward.
 */
import { addMinutes, differenceInMinutes } from "date-fns"

export interface SchedulableStop {
  id: string
  sequence: number
  plannedArrival: Date
  plannedDeparture: Date
  status?: string
  estimatedArrival?: Date
}

export interface AutoAdjustOptions {
  /** Leave stops the driver has completed where they are (monitoring). */
  skipCompleted?: boolean
  /** Move a stop's ETA with its planned arrival when it is moved or pushed (monitoring). */
  moveEstimate?: boolean
}

/** Minimum gap left between two stops. */
export const MIN_GAP_MINUTES = 5

export function autoAdjustStops<T extends SchedulableStop>(
  stops: T[],
  movedStopId: string,
  newArrival: Date,
  newDeparture: Date,
  { skipCompleted = false, moveEstimate = false }: AutoAdjustOptions = {},
): T[] {
  const result = [...stops]
  const movedIndex = result.findIndex((s) => s.id === movedStopId)
  if (movedIndex === -1) return result

  const retimed = (stop: T, start: Date, end: Date): T => ({
    ...stop,
    plannedArrival: start,
    plannedDeparture: end,
    ...(moveEstimate ? { estimatedArrival: start } : {}),
  })
  const isFixed = (stop: T) => skipCompleted && stop.status === "completed"

  result[movedIndex] = retimed(result[movedIndex], newArrival, newDeparture)

  // Sequence follows arrival order.
  const byArrival = [...result].sort(
    (a, b) => a.plannedArrival.getTime() - b.plannedArrival.getTime(),
  )
  byArrival.forEach((stop, idx) => {
    const originalIdx = result.findIndex((s) => s.id === stop.id)
    if (originalIdx !== -1) result[originalIdx] = { ...result[originalIdx], sequence: idx + 1 }
  })

  // Push anything overlapping the moved stop to whichever side it mostly sits on.
  const moved = result[movedIndex]
  const movedStart = moved.plannedArrival.getTime()
  const movedEnd = moved.plannedDeparture.getTime()
  for (let i = 0; i < result.length; i++) {
    const stop = result[i]
    if (stop.id === movedStopId || isFixed(stop)) continue
    const stopStart = stop.plannedArrival.getTime()
    const stopEnd = stop.plannedDeparture.getTime()
    if (!(stopStart < movedEnd && stopEnd > movedStart)) continue

    const duration = differenceInMinutes(stop.plannedDeparture, stop.plannedArrival)
    if ((stopStart + stopEnd) / 2 < (movedStart + movedEnd) / 2) {
      const end = addMinutes(new Date(movedStart), -MIN_GAP_MINUTES)
      result[i] = retimed(stop, addMinutes(end, -duration), end)
    } else {
      const start = addMinutes(new Date(movedEnd), MIN_GAP_MINUTES)
      result[i] = retimed(stop, start, addMinutes(start, duration))
    }
  }

  // Cascade remaining overlaps forward. Bounded, so a pathological input cannot spin.
  for (let iteration = 0; iteration < 10; iteration++) {
    let changed = false
    const ordered = result
      .filter((s) => !isFixed(s))
      .sort((a, b) => a.plannedArrival.getTime() - b.plannedArrival.getTime())
    for (let i = 0; i < ordered.length - 1; i++) {
      const current = ordered[i]
      const next = ordered[i + 1]
      if (differenceInMinutes(next.plannedArrival, current.plannedDeparture) >= MIN_GAP_MINUTES) {
        continue
      }
      changed = true
      const nextIdx = result.findIndex((s) => s.id === next.id)
      const duration = differenceInMinutes(next.plannedDeparture, next.plannedArrival)
      const start = addMinutes(current.plannedDeparture, MIN_GAP_MINUTES)
      result[nextIdx] = {
        ...result[nextIdx],
        plannedArrival: start,
        plannedDeparture: addMinutes(start, duration),
      }
    }
    if (!changed) break
  }

  return result
}
